/* eslint-disable consistent-return */
import { useCallback, useEffect } from "react";

import { useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { useShallow } from "zustand/react/shallow";

import { CommonInboundingListStatus, CommonListType } from "__graphql__/types";
import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import {
  GetInboundingListStatsDocument,
  GetInboundingListStatsQuery,
  GetInboundingListStatsQueryVariables,
} from "flows/Inbound/queries/collaborativeInbound/collaborativeInbound.generated";
import {
  GetPendingCheckCountDocument,
  GetPendingCheckCountQuery,
  GetPendingCheckCountQueryVariables,
} from "shared/queries/checkCount/checkCount.generated";
import {
  GetPublicRestockingListQuery,
  GetPublicRestockingListQueryVariables,
  GetPublicRestockingStatsDocument,
} from "shared/queries/publicRestockingList/publicRestockingList.generated";

const CANCELLED_ORDER_STATS_POLLING_INTERVAL_MS = 30000;
const POLLING_INTERVAL_OTHERS = 60000;

// Helper to check if within polling hours
// Polling should be active except between 2 AM and 6 AM
const POLLING_BREAK_START_HOUR = 2; // Break starts at 2 AM
const POLLING_BREAK_STOP_HOUR = 6; // Break ends at 6 AM

export function isInPollingHours() {
  const currentTime = new Date();
  const hour = currentTime.getHours();

  return !(hour >= POLLING_BREAK_START_HOUR && hour < POLLING_BREAK_STOP_HOUR);
}

const useConditionalPolling = (
  callback: () => void,
  intervalMs: number,
  shouldPollFn: () => boolean,
): void => {
  useEffect(() => {
    const poll = () => {
      const shouldPoll = shouldPollFn();
      if (shouldPoll) {
        callback();
      }
    };

    poll();

    const intervalId = setInterval(poll, intervalMs);
    return () => {
      clearInterval(intervalId);
    };
  }, [callback, intervalMs, shouldPollFn]);
};

export function useAppBackgroundEffects() {
  const { isAuthenticated } = useAuth0();
  const client = useApolloClient();
  const { setAppBackgroundEffectsStore } = useAppBackgroundEffectsStore(
    useShallow((state) => ({
      setAppBackgroundEffectsStore: state.setAppBackgroundEffectsStore,
    })),
  );

  // Always get freshest store state via getState inside polling condition function
  const getShouldPoll = useCallback((): boolean => {
    const { isIdentified } = useEmployeeStore.getState();
    const inHours = isInPollingHours();
    return isAuthenticated && isIdentified && inHours;
  }, [isAuthenticated]);

  // Fetch urgent checks count
  const fetchUrgentChecks = useCallback(async () => {
    const priority = [4, 5, 6, 7];
    const {
      data: { getOpenedChecksCountForHub },
    } = await client.query<GetPendingCheckCountQuery, GetPendingCheckCountQueryVariables>({
      query: GetPendingCheckCountDocument,
      variables: {
        input: { priority },
      },
      fetchPolicy: "network-only",
    });

    setAppBackgroundEffectsStore({
      hasOpenUrgentChecks: (getOpenedChecksCountForHub.checkCount ?? 0) > 0,
    });
  }, [client, setAppBackgroundEffectsStore]);

  // Fetch public restocking list
  const fetchRestockingList = useCallback(async () => {
    const {
      data: { getPublicRestockingList },
    } = await client.query<GetPublicRestockingListQuery, GetPublicRestockingListQueryVariables>({
      query: GetPublicRestockingStatsDocument,
      fetchPolicy: "network-only",
    });

    setAppBackgroundEffectsStore({
      publicRestockingItemCount:
        getPublicRestockingList?.publicRestockingList.restockingItems?.length ?? 0,
    });
  }, [client, setAppBackgroundEffectsStore]);

  // Fetch the count of unclaimed cancelled orders
  const fetchUnclaimedCancelledOrdersCount = useCallback(async () => {
    try {
      const {
        data: { getInboundingListStats },
      } = await client.query<GetInboundingListStatsQuery, GetInboundingListStatsQueryVariables>({
        query: GetInboundingListStatsDocument,
        variables: {
          input: {
            type: [CommonListType.list_type_cancelled_order],
            status: [CommonInboundingListStatus.inbounding_list_status_unclaimed],
          },
        },
        fetchPolicy: "network-only",
      });
      const count = getInboundingListStats?.stats?.[0]?.count ?? 0;

      setAppBackgroundEffectsStore({
        hasUnClaimedCancelledOrder: count > 0,
      });
    } catch (error) {
      datadogRum.addError(error, {
        message: "Failed to fetch unclaimed cancelled orders count",
      });
      setAppBackgroundEffectsStore({
        hasUnClaimedCancelledOrder: false,
      });
    }
  }, [client, setAppBackgroundEffectsStore]);

  // Conditional polling for all tasks
  useConditionalPolling(fetchUrgentChecks, POLLING_INTERVAL_OTHERS, getShouldPoll);
  useConditionalPolling(fetchRestockingList, POLLING_INTERVAL_OTHERS, getShouldPoll);
  useConditionalPolling(
    fetchUnclaimedCancelledOrdersCount,
    CANCELLED_ORDER_STATS_POLLING_INTERVAL_MS,
    getShouldPoll,
  );
}
